import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetArticleCommentsByID } from '@root/modules/article/types/article';
import getArticleCommentsByID from '@root/modules/article/graphql/getArticleCommentsByID.graphql';

import { ArticleCommentsAdapter } from '../utils/ArticleAdapter';
import { isValidSignedInt32 } from '@root/common/utils/helpers';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = { id: string | number };
type FetchVariables = GetArticleCommentsByID['variables'];
type Response = GetArticleCommentsByID['data'];

interface FetchArticleReturn {
  article: Response['article']['data'][0];
}

export default class ArticleForCommentsService extends Service implements ServiceInterface {
  private retriedFailedRequest = false;

  private fixVariables({ id }: ServiceVariables) {
    return {
      id: Number(id),
    };
  }

  private async fetchArticleForComments(variables: FetchVariables): Promise<FetchArticleReturn | undefined> {
    const options = Object.assign({ query: getArticleCommentsByID }, { variables });

    if (isNaN(Number(variables.id)) || !isValidSignedInt32(Number(variables.id))) {
      this.handleError({ message: 'article.error.not_found', statusCode: 400, extraData: 'invalid input `id`' });
      return;
    }

    try {
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'article_comments',
        },
      };

      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.article?.data.length) {
        this.handleError({ message: 'article.error.not_found', statusCode: 404 });
        return;
      }

      const articleAdapter = new ArticleCommentsAdapter(response.article.data[0]);
      const article = articleAdapter.adapt();

      return {
        article,
      };
    } catch (e) {
      // Retry failed request one time
      if (!this.retriedFailedRequest) {
        this.retriedFailedRequest = true;

        return await this.fetchArticleForComments(variables);
      }

      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: ServiceVariables) {
    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchArticleForComments(fixedVariables);

    return response;
  }
}

export function fetchArticleForComments(variables: ServiceVariables) {
  const articleForCommentsService = new ArticleForCommentsService();
  return articleForCommentsService.fetch(variables);
}
